import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import {
  Injectable,
  NgZone,
  PLATFORM_ID,
  inject,
  DOCUMENT,
} from '@angular/core';
import type { KeyboardStyle } from '@capacitor/keyboard';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { Pwa } from '@freelancer/pwa';
import type { Observable, Subscription } from 'rxjs';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';

const KEYBOARD_INSET_CSS_VAR = '--fl-keyboard-inset';

@Injectable({
  providedIn: 'root',
})
export class KeyboardService implements OnDestroy {
  private pwa = inject(Pwa);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);

  private isOpenSubject$ = new Subject<boolean>();
  private keyboardHeightSubject$ = new BehaviorSubject<number>(0);
  private androidKeyboardOverlapSubject$ = new BehaviorSubject<
    number | undefined
  >(undefined);
  private heightBeforeKeyboard = 0;
  private listenersAdded = false;
  private iosKeyboardInsetSubscription?: Subscription;
  private manualAvoidanceEnabled = false;

  isOpen(): Observable<boolean> {
    if (!this.listenersAdded && this.pwa.isNative()) {
      this.addListeners();
    }

    return this.isOpenSubject$.asObservable().pipe(startWith(false));
  }

  /**
   * Whether manual (CSS-driven) keyboard avoidance is currently active, i.e.
   * the iOS keyboard is in `KeyboardResize.None` mode so the WebView and
   * `window.visualViewport` no longer shrink when it opens. Consumers use this
   * to know they must rely on keyboard events / the `--fl-keyboard-inset` CSS
   * variable rather than the (now frozen) visual viewport.
   */
  isManualAvoidanceActive(): boolean {
    return this.isIosNative() && this.manualAvoidanceEnabled;
  }

  private isIosNative(): boolean {
    return this.pwa.isNative() && this.pwa.getPlatform() === 'ios';
  }

  private isAndroidNative(): boolean {
    return this.pwa.isNative() && this.pwa.getPlatform() === 'android';
  }

  /**
   * Height of the Android keyboard that covers the WebView, in px. Stays
   * `undefined` when the native app resizes the WebView for the keyboard.
   */
  androidKeyboardOverlap(): Observable<number | undefined> {
    if (!this.isAndroidNative()) {
      return of(undefined);
    }

    if (!this.listenersAdded) {
      this.addListeners();
    }

    return this.androidKeyboardOverlapSubject$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  private iosKeyboardHeight(): Observable<number> {
    if (!this.isIosNative()) {
      throw new Error('iosKeyboardHeight() called outside iOS');
    }

    if (!this.listenersAdded) {
      this.addListeners();
    }

    return this.keyboardHeightSubject$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  async addListeners(): Promise<void> {
    Keyboard.addListener('keyboardWillShow', info => {
      this.heightBeforeKeyboard = this.document.defaultView?.innerHeight ?? 0;
      this.ngZone.run(() => {
        this.isOpenSubject$.next(true);
        this.keyboardHeightSubject$.next(info.keyboardHeight);
      });
    });

    Keyboard.addListener('keyboardDidShow', async info => {
      const view = this.document.defaultView;
      if (!this.isAndroidNative() || !view) {
        return;
      }

      const height = view.innerHeight;
      let overlap = 0;
      if (height >= this.heightBeforeKeyboard) {
        const { top } = await EdgeToEdge.getInsets();
        overlap =
          top / view.devicePixelRatio +
          height +
          info.keyboardHeight -
          view.screen.height;
      }
      this.ngZone.run(() => {
        this.androidKeyboardOverlapSubject$.next(
          overlap > 0 ? overlap : undefined,
        );
      });
    });

    Keyboard.addListener('keyboardWillHide', () => {
      this.ngZone.run(() => {
        this.keyboardHeightSubject$.next(0);
        this.androidKeyboardOverlapSubject$.next(undefined);
      });
    });

    Keyboard.addListener('keyboardDidHide', () => {
      this.ngZone.run(() => {
        this.isOpenSubject$.next(false);
        this.keyboardHeightSubject$.next(0);
      });
    });

    this.listenersAdded = true;
  }

  /**
   * Set the visibility of the iOS keyboard accessory bar.
   * No-op on non-iOS platforms.
   */
  async setIosAccessoryBarVisible(isVisible: boolean): Promise<void> {
    if (!this.isIosNative()) {
      return;
    }

    Keyboard.setAccessoryBarVisible({ isVisible });
  }

  /**
   * Hide the iOS keyboard.
   * No-op on non-iOS platforms.
   */
  async hideOnIos(): Promise<void> {
    if (!this.isIosNative()) {
      return;
    }

    await Keyboard.hide();
  }

  /**
   * Toggle manual (CSS-driven) keyboard avoidance.
   * Used for smooth keyboard avoidance of fixed/sticky elements.
   * No-op on non-iOS platforms.
   */
  async setManualIosKeyboardAvoidance(enabled: boolean): Promise<void> {
    if (!this.isIosNative()) {
      return;
    }

    if (this.manualAvoidanceEnabled === enabled) {
      return;
    }
    this.manualAvoidanceEnabled = enabled;

    // Dismiss any open keyboard first. Switching resize mode mid-flight
    // leaves the WebView frame stuck at its previous size.
    await Keyboard.hide();
    await Keyboard.setResizeMode({
      mode: enabled ? KeyboardResize.None : KeyboardResize.Native,
    });

    if (!enabled) {
      this.iosKeyboardInsetSubscription?.unsubscribe();
      this.iosKeyboardInsetSubscription = undefined;
      this.document.documentElement.style.removeProperty(
        KEYBOARD_INSET_CSS_VAR,
      );
      return;
    }

    if (!this.iosKeyboardInsetSubscription) {
      this.iosKeyboardInsetSubscription = this.iosKeyboardHeight().subscribe(
        height => {
          this.document.documentElement.style.setProperty(
            KEYBOARD_INSET_CSS_VAR,
            `${height}px`,
          );
        },
      );
    }
  }

  /**
   * Set the style of the iOS keyboard (dark, light, or default).
   * No-op on non-iOS platforms.
   */
  async setIosStyle(style: KeyboardStyle): Promise<void> {
    if (!this.isIosNative()) {
      return;
    }

    await Keyboard.setStyle({ style });
  }

  /** Dismisses the soft keyboard on native app. */
  hide(): void {
    if (isPlatformBrowser(this.platformId) && this.pwa.isNative()) {
      Keyboard.hide();
    }
  }

  async removeListeners(): Promise<void> {
    Keyboard.removeAllListeners();
  }

  ngOnDestroy(): void {
    this.iosKeyboardInsetSubscription?.unsubscribe();
    if (this.listenersAdded && this.pwa.isNative()) {
      this.removeListeners();
    }
  }
}
